<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { LL } from "../../../i18n/i18n-svelte";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { ABSOLUTE_PUSHER_URL } from "../../Enum/ComputedConst";
    import { areCharacterTexturesValid } from "../../Connection/LocalUserUtils";
    import BodyIcon from "../Icons/BodyIcon.svelte";
    import EyesIcon from "../Icons/EyesIcon.svelte";
    import HairIcon from "../Icons/HairIcon.svelte";
    import HangerIcon from "../Icons/HangerIcon.svelte";
    import HatIcon from "../Icons/HatIcon.svelte";
    import SwordIcon from "../Icons/SwordIcon.svelte";
    import ShuffleIcon from "../Icons/ShuffleIcon.svelte";
    import WokaPreview from "./WokaPreview.svelte";
    import type { WokaBodyPart, WokaData, WokaTexture } from "./WokaTypes";
    import { getItemsPerRow } from "./ItemsPerRow";
    import WokaImage from "./WokaImage.svelte";

    interface Props {
        back: () => void;
        saveAndContinue: (texturesId: string[]) => void;
    }

    let { back, saveAndContinue }: Props = $props();

    let wokaData: WokaData | null = $state(null);
    let selectedBodyPart: WokaBodyPart = $state("body");
    let selectedTextures: Record<WokaBodyPart, string> = $state({
        body: "",
        eyes: "",
        hair: "",
        clothes: "",
        jacket: "",
        bottom: "",
        shoes: "",
        hat: "",
        accessory: "",
    });
    let isLoading = $state(true);
    let error = $state("");
    let assetsDirection: number = $state(0);

    // Selector en dos pasos: primero la forma (cada coleccion es una prenda, un
    // corte o una forma de ojo), despues su paleta de colores. La forma elegida
    // se recuerda por capa.
    let selectedCollection: Record<WokaBodyPart, number> = $state({
        body: 0,
        eyes: 0,
        hair: 0,
        clothes: 0,
        jacket: 0,
        bottom: 0,
        shoes: 0,
        hat: 0,
        accessory: 0,
    });

    // El orden de las pestañas es también el orden en que se guardan los ids,
    // así que agregar una capa al medio invalidaría los wokas ya guardados:
    // las nuevas van después de las que ya existían.
    const bodyPartOrder: WokaBodyPart[] = [
        "body",
        "eyes",
        "hair",
        "clothes",
        "hat",
        "accessory",
        "jacket",
        "bottom",
        "shoes",
    ];

    /** Las pestañas se rotulan con la clave del woka.json, que está en inglés
     *  y además ahora significa otra cosa: `clothes` es la prenda de arriba. */
    const NOMBRE_DE_CAPA: Record<WokaBodyPart, string> = {
        body: "Piel",
        eyes: "Ojos",
        hair: "Pelo",
        clothes: "Arriba",
        jacket: "Chaqueta",
        bottom: "Abajo",
        shoes: "Zapatos",
        hat: "Gorro",
        accessory: "Accesorio",
    };

    async function loadWokaData() {
        try {
            isLoading = true;
            wokaData = await gameManager.loadWokaData();

            loadSavedTextures();
        } catch (err) {
            console.error("Error loading Woka data:", err);
            error = "Failed to load Woka customization data";
        } finally {
            isLoading = false;
        }
    }

    function loadSavedTextures() {
        try {
            const savedTextureIds = gameManager.getCharacterTextureIds();
            // Check that textures exist in the Woka data
            if (!wokaData) {
                throw new Error("Woka data is not loaded");
            }

            // Check if current textures is in the collections list
            const hasCustomizeTexture = bodyPartOrder
                .flatMap((bodyPart) => wokaData?.[bodyPart]?.collections ?? [])
                .find((collection) =>
                collection.textures.find((texture) => savedTextureIds != null && savedTextureIds.includes(texture.id)),
            );

            // Initialize textures of customize Woka scene
            if (hasCustomizeTexture && savedTextureIds && savedTextureIds.length > 0) {
                bodyPartOrder.forEach((bodyPart, index) => {
                    if (savedTextureIds[index]) {
                        selectedTextures[bodyPart] = savedTextureIds[index];
                    }
                });
            } else {
                bodyPartOrder.forEach((bodyPart) => {
                    if (wokaData?.[bodyPart]?.collections?.[0]?.textures?.[0]) {
                        selectedTextures[bodyPart] = wokaData[bodyPart].collections[0].textures[0].id;
                    }
                });
            }
            // Trigger reactivity
            selectedTextures = { ...selectedTextures };
            syncSelectedCollections();
        } catch (err) {
            console.warn("Cannot load previous WOKA textures:", err);
        }
    }

    /** El color de la variante, si el catálogo lo publicó en `tags`.
     *
     * `tags` es el único campo libre que deja pasar el esquema del woka.json.
     * Cuando está, la paleta se dibuja como círculos de color en vez de
     * miniaturas del sprite, que en variantes de un mismo corte se distinguen
     * mal. Las texturas de fábrica no lo traen y siguen mostrando el sprite.
     */
    function colorDe(texture: WokaTexture): string | null {
        const tag = (texture.tags || []).find((t) => /^#[0-9a-f]{6}$/i.test(t));
        return tag ?? null;
    }

    /** Indice de la coleccion (la forma) a la que pertenece una textura. */
    function collectionOf(bodyPart: WokaBodyPart, textureId: string): number {
        const collections = wokaData?.[bodyPart]?.collections || [];
        const index = collections.findIndex((collection) =>
            collection.textures.some((texture) => texture.id === textureId),
        );
        return index < 0 ? 0 : index;
    }

    function syncSelectedCollections() {
        bodyPartOrder.forEach((bodyPart) => {
            selectedCollection[bodyPart] = collectionOf(bodyPart, selectedTextures[bodyPart]);
        });
        selectedCollection = { ...selectedCollection };
    }

    function selectCollection(collectionIndex: number) {
        const bodyPart = selectedBodyPart;
        const anterior = getAvailableTextures(selectedCollection[bodyPart], bodyPart);
        // Conserva la posicion en la paleta: cambiar de corte no cambia el tono,
        // porque todas las paletas del catalogo llevan los colores en el mismo
        // orden. Si la nueva es mas corta, cae en el ultimo color.
        const colorIndex = Math.max(
            0,
            anterior.findIndex((texture) => texture.id === selectedTextures[bodyPart]),
        );
        selectedCollection[bodyPart] = collectionIndex;
        selectedCollection = { ...selectedCollection };

        const texturas = getAvailableTextures(collectionIndex, bodyPart);
        if (texturas.length > 0) {
            selectTexture(bodyPart, texturas[Math.min(colorIndex, texturas.length - 1)].id);
        }
    }

    function selectTexture(bodyPart: WokaBodyPart, textureId: string) {
        selectedTextures[bodyPart] = textureId;
        selectedTextures = { ...selectedTextures };
    }

    function randomizeOutfit() {
        bodyPartOrder.forEach((bodyPart) => {
            // Sortea entre todas las colecciones, no solo la primera: con el
            // catalogo INSECAP la primera es la de fabrica.
            const textures = (wokaData?.[bodyPart]?.collections || []).flatMap(
                (collection) => collection.textures,
            );
            if (textures.length === 0) return;
            selectedTextures[bodyPart] = textures[Math.floor(Math.random() * textures.length)].id;
        });
        selectedTextures = { ...selectedTextures }; // Trigger reactivity
        syncSelectedCollections();
    }

    function handlerSaveAndContinue() {
        try {
            const textureIds = bodyPartOrder.map((bodyPart) => selectedTextures[bodyPart]).filter(Boolean);
            if (!areCharacterTexturesValid(textureIds)) {
                error = "Invalid character textures";
                return;
            }

            saveAndContinue(textureIds);
        } catch (err) {
            console.error("Error saving textures:", err);
            error = "Failed to save character customization";
        }
    }

    function getAvailableTextures(collectionIndex: number, bodyPart: WokaBodyPart): WokaTexture[] {
        const textures = wokaData?.[bodyPart]?.collections?.[collectionIndex]?.textures || [];
        // If no body texture is selected, return the first texture by default
        /*if (bodyPart === "body" && !textures.map((texture) => texture.id).includes(selectedTextures[bodyPart])) {
            selectTexture("body", textures[0].id);
        }*/

        return textures;
    }

    function getTextureUrl(relativeUrl: string): string {
        if (relativeUrl.startsWith("http://") || relativeUrl.startsWith("https://")) {
            return relativeUrl;
        }

        return `${ABSOLUTE_PUSHER_URL}${relativeUrl}`;
    }

    function getBodyPartIcon(bodyPart: WokaBodyPart) {
        switch (bodyPart) {
            case "body":
                return BodyIcon;
            case "eyes":
                return EyesIcon;
            case "hair":
                return HairIcon;
            case "clothes":
            case "jacket":
            case "bottom":
            case "shoes":
                return HangerIcon;
            case "hat":
                return HatIcon;
            case "accessory":
                return SwordIcon;
            default:
                return null;
        }
    }

    let timeOutToScroll: ReturnType<typeof setTimeout> | null = null;
    function selectBodyPart(bodyPart: WokaBodyPart) {
        selectedBodyPart = bodyPart;
        selectedCollection[bodyPart] = collectionOf(bodyPart, selectedTextures[bodyPart]);
        selectedCollection = { ...selectedCollection };

        // Clear previous timeout if it exists
        if (timeOutToScroll) clearTimeout(timeOutToScroll);
        timeOutToScroll = setTimeout(() => {
            timeOutToScroll = null; // Reset timeout variable
            // Check if texture to the body part selected is empty
            if (selectedTextures[selectedBodyPart] === "") return;
            // Get element by body part and texture id
            let element = document.getElementById(`texture-${selectedBodyPart}-${selectedTextures[selectedBodyPart]}`);
            // If element is not found, do nothing
            if (!element) {
                // Get the first texture of the body part element
                element = document.querySelector(
                    `#texture-${selectedBodyPart}-${wokaData?.[selectedBodyPart]?.collections?.[0]?.textures?.[0]?.id}`,
                );
                if (!element) return; // If still not found, exit
            }
            // Scroll to the element
            element.scrollIntoView({ behavior: "smooth", block: "end", inline: "end" });
        }, 100); // Delay to ensure the DOM is updated
    }

    let enterPressed = false;

    // Function to handle keyboard navigation
    function useKeyBoardNavigation(event: KeyboardEvent) {
        if (!wokaData) return;
        if (
            event.key === "ArrowLeft" ||
            event.key === "ArrowRight" ||
            event.key === "ArrowUp" ||
            event.key === "ArrowDown"
        ) {
            event.preventDefault();
            // Las flechas recorren la paleta de la forma elegida.
            const textures = getAvailableTextures(selectedCollection[selectedBodyPart], selectedBodyPart);
            const currentIndex = textures.findIndex((texture) => texture.id === selectedTextures[selectedBodyPart]);
            let newIndex = currentIndex;
            if (event.key === "ArrowLeft") {
                newIndex = Math.max(currentIndex - 1, 0);
            } else if (event.key === "ArrowRight") {
                newIndex = Math.min(currentIndex + 1, textures.length - 1);
            } else if (event.key === "ArrowUp") {
                const itemsPerRow = getItemsPerRow(document.getElementById(`woka-line-0`));
                newIndex = Math.max(currentIndex - itemsPerRow, 0);
            } else if (event.key === "ArrowDown") {
                const itemsPerRow = getItemsPerRow(document.getElementById(`woka-line-0`));
                newIndex = Math.min(currentIndex + itemsPerRow, textures.length - 1);
            }
            if (newIndex !== currentIndex) {
                selectTexture(selectedBodyPart, textures[newIndex].id);
                // Scroll to the newly selected texture
                const element = document.getElementById(`texture-${selectedBodyPart}-${textures[newIndex].id}`);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
                }
            }
        } else if (event.key === "Enter") {
            enterPressed = true;
        }
    }

    function useKeyBoardNavigationUp(event: KeyboardEvent) {
        if (!wokaData) return;
        if (event.key === "Enter" && enterPressed) {
            enterPressed = false;
            if (selectedBodyPart === bodyPartOrder[bodyPartOrder.length - 1]) {
                // If it's the last body part, save and continue
                handlerSaveAndContinue();
            } else {
                selectNextBodyPart();
            }
        }
    }

    function selectNextBodyPart() {
        const currentIndex = bodyPartOrder.indexOf(selectedBodyPart);
        const nextIndex = (currentIndex + 1) % bodyPartOrder.length;
        selectedBodyPart = bodyPartOrder[nextIndex];
    }

    onMount(async () => {
        await loadWokaData();
        selectedBodyPart = bodyPartOrder[0];
        // Document event listener for keyboard navigation
        document.addEventListener("keydown", useKeyBoardNavigation);
        document.addEventListener("keyup", useKeyBoardNavigationUp);
    });

    onDestroy(() => {
        // Remove keyboard navigation listener
        document.removeEventListener("keydown", useKeyBoardNavigation);
        document.removeEventListener("keyup", useKeyBoardNavigationUp);
    });
</script>

<div class="mobile-webkit bg-contrast w-screen md:!mt-[15vh] h-full md:!h-[70vh] flex items-center justify-center">
    <div
        class="mobile-webkit rounded-lg flex flex-col max-w-4xl w-full h-full m-4 relative bg-white/10 backdrop-blur-md"
    >
        {#if isLoading}
            <div class="flex items-center justify-center h-64">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        {:else if error}
            <div class="text-center text-red-600 mb-4">
                <p>{error}</p>
                <button class="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600" onclick={loadWokaData}>
                    Retry
                </button>
            </div>
        {:else}
            <div class="flex-1 flex flex-col sm:flex-row items-start gap-6 min-h-0 p-6">
                <div class="flex flex-row gap-4 w-full sm:w-fit">
                    <div class="flex flex-col gap-2">
                        <WokaPreview
                            {selectedTextures}
                            {wokaData}
                            {getTextureUrl}
                            onrotate={(direction) => {
                                assetsDirection = direction;
                            }}
                        />

                        <div class="mt-4 space-y-2">
                            <button
                                class="btn btn-sm btn-light btn-border w-full px-4 py-2 bg-white/10 text-white rounded hover:bg-white/10 flex flex-row items-center justify-center gap-2"
                                onclick={randomizeOutfit}
                            >
                                <ShuffleIcon fillColor="white" width="w-4" height="h-4" />
                                <span>{$LL.woka.customWoka.randomize()}</span>
                            </button>
                        </div>
                    </div>
                    <div class="flex flex-col gap-0 mb-4 w-full lg:w-fit sm:hidden">
                        {#each bodyPartOrder as bodyPart (bodyPart)}
                            {@const BodyPartIcon = getBodyPartIcon(bodyPart)}
                            <button
                                class="flex-1 px-4 py-2 flex-grow capitalize flex flex-row items-center justify-center gap-2 border-b-2 {selectedBodyPart ===
                                bodyPart
                                    ? 'text-white border-white'
                                    : 'text-white/50 border-white/10'}"
                                onclick={() => selectBodyPart(bodyPart)}
                                style="border-bottom-style: solid;"
                            >
                                <BodyPartIcon
                                    height="h-5"
                                    width="w-5"
                                    strokeColor={selectedBodyPart === bodyPart ? "stroke-white " : "stroke-white/50"}
                                    fillColor={selectedBodyPart === bodyPart ? "fill-white " : "fill-white/50"}
                                />
                                {NOMBRE_DE_CAPA[bodyPart] ?? bodyPart}
                            </button>
                        {/each}
                    </div>
                </div>

                <div class="flex flex-col flex-1 h-full min-h-0 min-w-0">
                    <div class="flex-wrap gap-0 mb-4 w-full sm:flex hidden">
                        {#each bodyPartOrder as bodyPart (bodyPart)}
                            {@const BodyPartIcon = getBodyPartIcon(bodyPart)}
                            <button
                                class="flex-1 px-4 py-2 capitalize flex flex-row items-center justify-center gap-2 border-b-2 {selectedBodyPart ===
                                bodyPart
                                    ? 'text-white border-white'
                                    : 'text-white/50 border-white/10'}"
                                onclick={() => selectBodyPart(bodyPart)}
                                style="border-bottom-style: solid;"
                            >
                                <BodyPartIcon
                                    height="h-5"
                                    width="w-5"
                                    strokeColor={selectedBodyPart === bodyPart ? "stroke-white " : "stroke-white/50"}
                                    fillColor={selectedBodyPart === bodyPart ? "fill-white " : "fill-white/50"}
                                />
                                {NOMBRE_DE_CAPA[bodyPart] ?? bodyPart}
                            </button>
                        {/each}
                    </div>

                    <div class="rounded-lg flex flex-col flex-1 min-h-0 min-w-0">
                        <h3 class="text-lg font-semibold capitalize">
                            {NOMBRE_DE_CAPA[selectedBodyPart] ?? selectedBodyPart}
                            {#if wokaData?.[selectedBodyPart]?.collections?.[selectedCollection[selectedBodyPart]]}
                                <span class="text-sm font-normal normal-case text-white/60"
                                    >&middot; {wokaData[selectedBodyPart].collections[
                                        selectedCollection[selectedBodyPart]
                                    ].name}</span
                                >
                            {/if}
                        </h3>
                        <div class="flex-none lg:flex-1 flex flex-row gap-4 min-h-0 min-w-0 max-h-full py-[20px]">
                            <!-- Paso 1: la forma. Cada coleccion del woka.json es una prenda, un
                                 corte o una forma de ojo, y se muestra con su primera textura. -->
                            <div
                                class="w-28 sm:w-36 shrink-0 flex flex-col items-stretch gap-1 min-h-0 overflow-y-auto overflow-x-hidden scroll-mask"
                            >
                                {#each wokaData?.[selectedBodyPart]?.collections || [] as collection, collectionIndex (collection.name)}
                                    <button
                                        class="rounded border border-solid box-border p-1 flex flex-row items-center gap-2 text-left {selectedCollection[
                                            selectedBodyPart
                                        ] === collectionIndex
                                            ? 'bg-white/50 border-white'
                                            : 'bg-white/10 hover:bg-white/20 border-transparent'}"
                                        onclick={() => selectCollection(collectionIndex)}
                                        id={`collection-${selectedBodyPart}-${collectionIndex}`}
                                    >
                                        {#if collection.textures[0]}
                                            <WokaImage
                                                selectedTextures={{ [selectedBodyPart]: collection.textures[0].id }}
                                                {wokaData}
                                                {getTextureUrl}
                                                canvasSize={40}
                                                direction={assetsDirection}
                                            />
                                        {/if}
                                        <span class="text-xs leading-tight break-words">{collection.name}</span>
                                    </button>
                                {/each}
                            </div>

                            <!-- Paso 2: la paleta de esa forma. -->
                            <div
                                class="flex-1 flex flex-row flex-wrap items-start justify-start content-start gap-3 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden scroll-mask"
                                id="woka-line-0"
                            >
                                {#each getAvailableTextures(selectedCollection[selectedBodyPart], selectedBodyPart) as texture (texture.id)}
                                    {@const color = colorDe(texture)}
                                    <button
                                        class="rounded border border-solid box-border h-fit {color
                                            ? 'p-1'
                                            : 'p-0'} {selectedTextures[selectedBodyPart] === texture.id
                                            ? 'bg-white/50 border-white'
                                            : 'bg-white/10 hover:bg-white/20 border-transparent'}"
                                        onclick={() => selectTexture(selectedBodyPart, texture.id)}
                                        id={`texture-${selectedBodyPart}-${texture.id}`}
                                        title={texture.name}
                                        aria-label={texture.name}
                                    >
                                        {#if color}
                                            <span
                                                class="block w-8 h-8 rounded-full border border-black/30"
                                                style="background-color: {color};"
                                            ></span>
                                        {:else}
                                            <WokaImage
                                                selectedTextures={{ [selectedBodyPart]: texture.id }}
                                                {wokaData}
                                                {getTextureUrl}
                                                classList="p-2"
                                                direction={assetsDirection}
                                            />
                                        {/if}
                                    </button>
                                {/each}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="w-full p-3 flex flex-row items-center gap-2 border-t-2 border-t-white/10"
                style="border-top-style: solid;"
            >
                <button class="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded" onclick={back}>
                    {$LL.woka.customWoka.navigation.backToDefaultWoka()}
                </button>
                <button
                    class="selectCharacterSceneFormSubmit w-full px-4 py-3 bg-secondary text-white rounded hover:bg-secondary-600"
                    onclick={handlerSaveAndContinue}
                >
                    {$LL.woka.customWoka.navigation.finish()}
                </button>
            </div>
        {/if}
    </div>
</div>

<style>
    .mobile-webkit {
        max-height: -webkit-fill-available !important;
    }
    .scroll-mask {
        mask-image: linear-gradient(to bottom, transparent 0px, black 40px, black calc(100% - 40px), transparent 100%);
        -webkit-mask-image: linear-gradient(
            to bottom,
            transparent 0px,
            black 40px,
            black calc(100% - 40px),
            transparent 100%
        );
    }
</style>
